
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import AudioGroupViewModel from '@models/meditations/create/audioGroupViewModel';
import MeditationAudioViewModel from '@models/meditations/create/meditationAudioViewModel';
import { speechErrorMessage, SpeechResult } from '@/api/dataSource/api/Spirit/Admin/Services/SpeechService';

@Component
export default class SpeechPhraseGroupControls extends Vue {
  @Prop({ required: true }) audioGroup: AudioGroupViewModel;
  @Prop({ default: false }) disabled: boolean;
  @Prop({ default: false }) dirty: boolean;
  loading = false;
  processed = 0;
  total = 0;
  cancelled = false;
  currentLanguage = '';
  errors: { id: string; language: string; message: string }[] = [];
  retryIds: string[] = [];
  retryForce = false;
  showErrors = false;
  notice = '';
  private disposed = false;
  private generationId = 0;
  private abortController: AbortController = null;
  private pendingIds: string[] = [];
  @Watch('audioGroup')
  resetProgress() {
    if (this.loading) return;
    this.processed = 0;
    this.total = 0;
    this.retryIds = [];
    this.retryForce = false;
    this.errors = [];
    this.showErrors = false;
    this.cancelled = false;
    this.notice = '';
  }
  get items(): MeditationAudioViewModel[] {
    const ids: string[] = [];
    return (this.audioGroup.audios || []).filter(item => {
      if (!item.id || !(item.description || '').trim() || ids.indexOf(item.id) !== -1) return false;
      ids.push(item.id);
      return true;
    });
  }
  get hasAudio(): boolean { return this.items.some(item => !!(item.femaleUrl || item.maleUrl)); }
  get readyCount(): number { return this.items.filter(item => !!(item.femaleUrl && item.maleUrl)).length; }
  get allCreated(): boolean { return !!this.items.length && this.readyCount === this.items.length; }
  get hasStaleAudio(): boolean { return this.items.some(item => item.speechNeedsUpdate); }
  beforeDestroy() {
    this.disposed = true;
    this.cancelGeneration();
  }
  cancelGeneration() {
    if (!this.loading) return;
    this.cancelled = true;
    this.generationId++;
    this.pendingIds.forEach(id => { if (this.retryIds.indexOf(id) === -1) this.retryIds.push(id); });
    this.pendingIds = [];
    const controller = this.abortController;
    this.abortController = null;
    if (controller) controller.abort();
    this.loading = false;
    this.currentLanguage = '';
    this.showErrors = false;
    this.notice = '';
    this.$emit('busy', false);
  }
  private waitForGeneration(id: string, force: boolean, signal: AbortSignal) {
    // Also settle locally when an upstream transport does not honor cancellation.
    return new Promise<SpeechResult>((resolve, reject) => {
      const abort = () => {
        const error = new Error('Ожидание остановлено');
        error.name = 'AbortError';
        signal.removeEventListener('abort', abort);
        reject(error);
      };
      if (signal.aborted) { abort(); return; }
      signal.addEventListener('abort', abort);
      try {
        this.$api.SpeechService.generateAsync(id, force, { signal }).then(result => {
          signal.removeEventListener('abort', abort);
          resolve(result);
        }, error => {
          signal.removeEventListener('abort', abort);
          reject(error);
        });
      } catch (error) {
        signal.removeEventListener('abort', abort);
        reject(error);
      }
    });
  }
  async generate(force: boolean = false) {
    if (this.disposed || this.loading || this.disabled || this.dirty) return;
    const items = this.items.filter(item => force || !this.retryIds.length || this.retryIds.indexOf(item.id) !== -1);
    if (!items.length) return;
    const requestForce = !!(force || this.retryForce && this.retryIds.length > 0);
    const runId = ++this.generationId;
    const controller = new AbortController();
    this.abortController = controller;
    this.pendingIds = items.map(item => item.id);
    this.retryForce = requestForce;
    this.loading = true;
    this.$emit('busy', true);
    this.processed = 0;
    this.total = items.length;
    this.cancelled = false;
    this.notice = '';
    this.errors = [];
    this.showErrors = false;
    this.retryIds = [];
    try {
      for (let index = 0; index < items.length; index++) {
        if (this.disposed || runId !== this.generationId) break;
        if (this.cancelled) {
          this.retryIds.push(...items.slice(index).map(item => item.id));
          break;
        }
        const item = items[index];
        this.currentLanguage = (item.langCode || '').toUpperCase();
        try {
          const result = await this.waitForGeneration(item.id, requestForce, controller.signal);
          if (this.disposed || runId !== this.generationId) break;
          item.femaleUrl = result.femaleUrl;
          item.maleUrl = result.maleUrl;
          item.speechNeedsUpdate = false;
          this.$emit('generated', result);
        } catch (error) {
          if (this.disposed || runId !== this.generationId) break;
          this.errors.push({ id: item.id, language: item.langCode || '', message: speechErrorMessage(error) });
          this.retryIds.push(item.id);
          if (error && error.name === 'TimeoutError') {
            this.notice = speechErrorMessage(error);
            this.retryIds.push(...items.slice(index + 1).map(item => item.id));
            this.processed++;
            break;
          }
        }
        this.processed++;
        this.pendingIds = items.slice(index + 1).map(item => item.id);
      }
    } finally {
      if (runId === this.generationId) {
        this.abortController = null;
        this.pendingIds = [];
        this.loading = false;
        this.currentLanguage = '';
        this.$emit('busy', false);
      }
    }
  }
}
